import { FormSection } from '../FormSection';
import {
  TravellerWithCollectionMembershipsDTO,
  useGetDepartmentByID,
  CardCreditDTO,
} from '@/api/trafikportalen';
import { FormattedMessage, useIntl } from 'react-intl';
import { DialogWindow } from '../Dialog';
import { useMemo, useState } from 'react';
import Button from '../core/Button';
import IconButton from '../core/IconButton';
import { IconTrash, IconSmartcard, IconEdit, IconInfo } from '@/icons';
import { CreditCardUpdateForm } from './CreditCardUpdateForm';
import {
  formatCardNumber,
  formatDate,
  formatDateTime,
} from '@/helpers/formatting';
import { CreditCardCreateForm } from './CreditCardCreateForm';
import { CreditCardDeleteForm } from './CreditCardDeleteForm';
import { getTravellerStatus } from '@/helpers/status';
import {
  hasAnyMembershipDepartmentWriteAccess,
  hasDepartmentWriteAccess,
} from '@/helpers/departmentPermissions';
import { useListTravellerCardCredits } from '@/hooks/useTravellerCreditInfo';
import { checkUserPermissions } from '../Auth/checkUserPermissions';

interface Props {
  traveller: TravellerWithCollectionMembershipsDTO;
}

export const CreditCards = ({ traveller }: Props) => {
  const { formatMessage, locale } = useIntl();

  const [addCardOpen, setAddCardOpen] = useState(false);

  const { cards } = useListTravellerCardCredits(traveller);

  const totalCredit = useMemo(() => {
    return cards.reduce((acc, card) => acc + card.credit, 0);
  }, [cards]);

  const status = getTravellerStatus(traveller);

  const departmentsWithoutCards = useMemo(() => {
    return traveller.memberships.filter(
      (membership) =>
        membership.active &&
        !cards.some((card) => card.department_id === membership.department_id),
    );
  }, [traveller.memberships, cards]);

  const hasDepartmentsWithoutCards = useMemo(() => {
    return departmentsWithoutCards.some((membership) =>
      hasDepartmentWriteAccess(membership.department_id),
    );
  }, [departmentsWithoutCards]);

  return (
    <>
      <FormSection
        id="credit-cards"
        title={
          <div className="flex flex-wrap items-center gap-x-2">
            <IconSmartcard className="size-8 min-w-8 text-smartcard" />
            <FormattedMessage defaultMessage="Tjänsteresa på kort" />
            <DialogWindow
              title={formatMessage({ defaultMessage: 'Tjänsteresa på kort' })}
              content={
                <div className="flex flex-col gap-2">
                  <p>
                    <FormattedMessage defaultMessage="Ge resenärer i organisationen möjlighet att använda ett fysiskt resekort för att betala för tjänsteresor." />
                  </p>
                  <p>
                    <FormattedMessage defaultMessage="Korten måste vara helt 'blanka' kort. De får inte tillhöra någon profil i resesystemet. Kort som använts för tjänsteresa i appen (utan att ha använts för något annat därefter) kan återanvändas för tjänsteresa på kort igen." />
                  </p>
                  <p>
                    <FormattedMessage defaultMessage="Den kredit som sätts är hur mycket som kortet fylls på med direkt. I början av varje månad toppas kortet upp med så mycket som det rests för, vilket är så mycket organisationen får betala för. Denna påfyllning sker i början av varje månad vilket gör att transaktionen för detta blir fakturerad en månad senare än förväntat." />
                  </p>
                  <p>
                    <FormattedMessage defaultMessage="Om gränsen för tjänsteresa på kort ändras sker en påfyllning/avstämning direkt, vilket kan leda till att en transaktion skapas direkt. Om transaktion skapas direkt kommer den med i närmaste fakturering. Vid sänkning av gräns får personer tillgång till nya gränsbeloppet, oavsett hur mycket som hade nyttjats tidigare." />
                  </p>
                </div>
              }
              dialogTrigger={
                <IconButton
                  aria-label={formatMessage({
                    defaultMessage: 'Visa information om tjänsteresa på kort',
                  })}
                  icon={<IconInfo />}
                  variant="ghost"
                  className="ml-auto"
                />
              }
            />
          </div>
        }
      >
        {cards.length > 1 && (
          <p className="border-b border-b-background-200 pb-2 text-text-800">
            <FormattedMessage defaultMessage="Total kredit på korten:" />{' '}
            <span className="font-semibold">
              {totalCredit.toLocaleString(locale)}
            </span>{' '}
            <FormattedMessage defaultMessage="SEK" />
          </p>
        )}
        {status === 'inactive' ? (
          <i className="pb-2 text-text-700">
            <FormattedMessage defaultMessage="Resenären är inaktiv och kan därmed inte ha tjänsteresa på kort" />
          </i>
        ) : cards.length === 0 ? (
          <i className="pb-2 text-text-700">
            <FormattedMessage defaultMessage="Ingen tjänsteresa på kort för den här resenären" />
          </i>
        ) : null}
        <div className="flex flex-col">
          {cards.map((card) => (
            <CreditCard
              key={card.BobcatWalletID}
              card={card}
              traveller={traveller}
            />
          ))}
        </div>
        {hasAnyMembershipDepartmentWriteAccess(traveller) &&
        hasDepartmentsWithoutCards ? (
          <Button
            onClick={() => setAddCardOpen(true)}
            disabled={status === 'inactive'}
          >
            <FormattedMessage defaultMessage="Lägg till tjänsteresa på kort" />
          </Button>
        ) : null}
        {status !== 'inactive' && !hasDepartmentsWithoutCards ? (
          <i className="text-text-700">
            {checkUserPermissions({ permission: 'organization:editor' }) ? (
              <FormattedMessage defaultMessage="Resenären har redan tjänsteresa på kort för alla sina avdelningar." />
            ) : (
              <FormattedMessage defaultMessage="Resenären har redan tjänsteresa på kort för alla sina avdelningar (som du har skrivrättighet för)." />
            )}
          </i>
        ) : null}
      </FormSection>
      <DialogWindow
        title={formatMessage({
          defaultMessage: 'Lägg till tjänsteresa på kort',
        })}
        content={
          <CreditCardCreateForm
            onOpenChange={setAddCardOpen}
            traveller={traveller}
          />
        }
        onOpenChange={setAddCardOpen}
        open={addCardOpen}
      />
    </>
  );
};

const CreditCard = ({
  card,
  traveller,
}: {
  card: CardCreditDTO;
  traveller: TravellerWithCollectionMembershipsDTO;
}) => {
  const { data: department } = useGetDepartmentByID(card.department_id);

  const { formatMessage, locale } = useIntl();

  const [updateOpen, setUpdateOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const showUpdatedDate = card.created_at !== card.updated_at;

  return (
    <div className="mb-2 flex gap-2 border-b border-b-background-200 pb-2 last:border-b-0">
      <IconSmartcard className="size-5 min-w-5" />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex w-full flex-col sm:flex-row sm:items-center sm:gap-5">
          <p className="whitespace-nowrap">
            {card.credit.toLocaleString(locale)}{' '}
            <span className="text-text-800">
              <FormattedMessage defaultMessage="SEK" />
            </span>
          </p>
          <p className="min-w-0 flex-1 truncate">{department?.name}</p>
        </div>
        <span className="font-mono text-text-700">
          {formatCardNumber(card.card_serial ?? '')}
        </span>
        <div className="flex gap-3 text-text-700">
          <span title={formatDateTime(card.created_at)}>
            <FormattedMessage
              defaultMessage="Skapad {date}"
              values={{ date: formatDate(card.created_at) }}
            />
          </span>
          {showUpdatedDate && (
            <span title={formatDateTime(card.updated_at)}>
              <FormattedMessage
                defaultMessage="Uppdaterad {date}"
                values={{ date: formatDate(card.updated_at) }}
              />
            </span>
          )}
        </div>
      </div>
      {hasDepartmentWriteAccess(card.department_id) && (
        <div className="ml-auto flex gap-1">
          <IconButton
            aria-label={formatMessage({ defaultMessage: 'Redigera' })}
            icon={<IconEdit />}
            variant="ghost"
            onClick={() => setUpdateOpen(true)}
          />
          <IconButton
            aria-label={formatMessage({ defaultMessage: 'Ta bort' })}
            icon={<IconTrash />}
            variant="ghost"
            color="red"
            onClick={() => setDeleteOpen(true)}
          />
        </div>
      )}
      <DialogWindow
        title={formatMessage({ defaultMessage: 'Uppdatera kreditgräns' })}
        content={
          <CreditCardUpdateForm
            card={card}
            traveller={traveller}
            onOpenChange={setUpdateOpen}
          />
        }
        onOpenChange={setUpdateOpen}
        open={updateOpen}
      />
      <DialogWindow
        title={formatMessage({
          defaultMessage: 'Ta bort tjänsteresa på kort',
        })}
        content={
          <CreditCardDeleteForm card={card} onOpenChange={setDeleteOpen} />
        }
        onOpenChange={setDeleteOpen}
        open={deleteOpen}
      />
    </div>
  );
};
